import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { tokenValue } from '../../lib/token-value';

// Lime dots fly from a source element (MilestoneCard, AgentAssembleCard) to the
// AgentsIslandBadge pill. On arrival the pill's sound wave starts animating.
// Pattern mirrors MilestoneCard's canvas confetti — a fixed-position canvas
// overlay runs a rAF loop for the flight, then self-clears.

const LIME_TOKEN = 'var(--ds-fill-accent-brand-default)';
const COUNT = 5;
const DOT_R = 7;
// Parity-pinned with the native apps (iOS FlyToPillController / Android FlyToPill.kt):
// the original 700ms flight read as "too fast, not connected to the pill".
const FLIGHT_MS = 1100;
const STAGGER_MS = 80;
// The perpendicular arc bow never collapses below this — short flights still curve visibly.
const MIN_BOW_PX = 60;

function bezier(t: number, p0: number, p1: number, p2: number): number {
  return (1 - t) ** 2 * p0 + 2 * (1 - t) * t * p1 + t ** 2 * p2;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function runFlight(
  canvas: HTMLCanvasElement,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  // Canvas takes a colour string, not a CSS value — resolve the token once per flight.
  const lime = tokenValue(LIME_TOKEN);

  // Arc control point: offset PERPENDICULAR to the flight direction ((-dy, dx), i.e. rotated
  // 90°), scaled to 30% of the path length with a floor — so the arc bows visibly on any path
  // angle (the old midpoint-lift collapsed on near-vertical badge→pill flights). Mirrors native.
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const perpScale = Math.max(dist * 0.3, MIN_BOW_PX) / Math.max(dist, 1);
  const cx = (sx + tx) / 2 + -dy * perpScale;
  const cy = (sy + ty) / 2 + dx * perpScale;

  const start = performance.now();
  const totalDuration = FLIGHT_MS + (COUNT - 1) * STAGGER_MS + 60;

  const tick = (now: number) => {
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = false;

    for (let i = 0; i < COUNT; i++) {
      const elapsed = now - start - i * STAGGER_MS;
      if (elapsed < 0) { alive = true; continue; }
      const raw = Math.min(1, elapsed / FLIGHT_MS);
      const et = easeInOut(raw);
      // Lateral spread fans out from center then converges to pill center.
      const spread = (i - (COUNT - 1) / 2) * 7 * (1 - et);
      const x = bezier(et, sx, cx, tx) + spread;
      const y = bezier(et, sy, cy, ty);
      const r = DOT_R * (0.75 + 0.5 * Math.sin(Math.PI * raw));
      // Opaque through 88% of the arc, then fade — so dots visibly ARRIVE at the pill.
      const alpha = raw > 0.88 ? 1 - (raw - 0.88) / 0.12 : 1;
      if (raw < 1) alive = true;
      ctx.globalAlpha = alpha;
      ctx.shadowColor = lime;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = lime;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    ctx.globalAlpha = 1;
    if (alive && now - start < totalDuration) {
      requestAnimationFrame(tick);
    } else {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    }
  };

  requestAnimationFrame(tick);
}

interface FlyAPI {
  setPillEl: (el: HTMLElement | null) => void;
  triggerFly: (sourceEl: HTMLElement) => void;
}

const FlyCtx = createContext<FlyAPI>({ setPillEl: () => {}, triggerFly: () => {} });

export function FlyToPillProvider({ children }: { children: React.ReactNode }) {
  const pillEl = useRef<HTMLElement | null>(null);
  const canvasEl = useRef<HTMLCanvasElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const setPillEl = useCallback((el: HTMLElement | null) => {
    pillEl.current = el;
  }, []);

  const triggerFly = useCallback((sourceEl: HTMLElement) => {
    const canvas = canvasEl.current;
    if (!canvas) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced) return;
    const sr = sourceEl.getBoundingClientRect();
    // Fall back to the top-right corner (where the pill lives) when no real pill is registered.
    const tr = pillEl.current?.getBoundingClientRect() ?? {
      left: window.innerWidth - 120,
      top: 28,
      width: 100,
      height: 36,
    };
    runFlight(canvas, sr.left + sr.width / 2, sr.top + sr.height / 2, tr.left + tr.width / 2, tr.top + tr.height / 2);
  }, []);

  return (
    <FlyCtx.Provider value={{ setPillEl, triggerFly }}>
      {children}
      {/* `document.body` can be null when something outside the app has replaced the document (seen in
          Sentry: "Target container is not a DOM element"), and a decorative canvas must not take the tree down. */}
      {mounted &&
        document.body &&
        createPortal(
          <canvas
            ref={canvasEl}
            aria-hidden
            style={{
              position: 'fixed',
              inset: 0,
              width: '100vw',
              height: '100vh',
              pointerEvents: 'none',
              zIndex: 9998,
            }}
          />,
          document.body,
        )}
    </FlyCtx.Provider>
  );
}

export function useFlyToPill(): (sourceEl: HTMLElement) => void {
  return useContext(FlyCtx).triggerFly;
}

export function useRegisterPillEl(): (el: HTMLElement | null) => void {
  return useContext(FlyCtx).setPillEl;
}
